/**
 * 培训业务管理页面
 */

import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Alert,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExportOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { TrainingBusiness, TrainingBusinessFormData, PaginationParams } from '../../types';
import { 
  getTrainingBusinesses, 
  createTrainingBusiness, 
  updateTrainingBusiness, 
  deleteTrainingBusiness,
  getTrainingBusinessStats,
  deleteAllTrainingBusinesses,
  exportTrainingBusinesses
} from '../../services/api';
import { formatDateTime } from '../../utils/helpers';
import { exportTrainingBusinessesToExcel, showConfirmDialog } from '../../utils/exportUtils';

const { TextArea } = Input;

const TrainingBusinesses: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState<TrainingBusiness[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  
  // 统计数据状态
  const [totalCount, setTotalCount] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // 模态框状态
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<TrainingBusiness | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadBusinesses();
    loadStats();
  }, [currentPage, pageSize, searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  // 加载培训业务列表
  const loadBusinesses = async () => {
    try {
      setLoading(true);
      const params: PaginationParams = {
        page: currentPage,
        limit: pageSize,
        search: searchText || undefined,
      };
      
      const data = await getTrainingBusinesses(params);
      setBusinesses(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      console.error('加载培训业务列表失败:', err);
      message.error(err instanceof Error ? err.message : '加载培训业务列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载统计数据
  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const data = await getTrainingBusinessStats();
      setTotalCount(data.total);
    } catch (err) {
      console.error('加载统计数据失败:', err);
      message.error(err instanceof Error ? err.message : '加载统计数据失败');
    } finally {
      setStatsLoading(false);
    }
  };

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  // 打开新增模态框
  const handleAdd = () => {
    setEditingBusiness(null);
    setModalVisible(true);
    form.resetFields();
  };

  // 打开编辑模态框
  const handleEdit = (business: TrainingBusiness) => {
    setEditingBusiness(business);
    setModalVisible(true);
    form.setFieldsValue({
      name: business.name,
      description: business.description,
    });
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingBusiness) {
        // 编辑培训业务
        const formData: TrainingBusinessFormData = {
          name: values.name.trim(),
          description: values.description?.trim() || '',
        };
        await updateTrainingBusiness(editingBusiness.id, formData);
        message.success('编辑培训业务成功');
      } else {
        // 批量新增培训业务
        const businessNames = values.name.split('、').map((name: string) => name.trim()).filter(Boolean);
        
        if (businessNames.length === 0) {
          message.error('请输入至少一个培训业务名称');
          return;
        }

        let successCount = 0;
        let failedBusinesses: string[] = [];

        for (const businessName of businessNames) {
          try {
            const formData: TrainingBusinessFormData = {
              name: businessName,
              description: values.description?.trim() || '',
            };
            
            await createTrainingBusiness(formData);
            successCount++;
          } catch (err) {
            console.error(`创建培训业务 ${businessName} 失败:`, err);
            failedBusinesses.push(businessName);
          }
        }

        if (successCount > 0) {
          message.success(`成功创建 ${successCount} 个培训业务`);
          if (failedBusinesses.length > 0) {
            message.warning(`以下培训业务创建失败: ${failedBusinesses.join('、')}`);
          }
          setModalVisible(false);
          loadBusinesses();
          loadStats();
        } else {
          message.error('所有培训业务创建失败，请检查输入');
        }
      }
    } catch (err) {
      console.error('提交失败:', err);
      if (err instanceof Error) {
        message.error(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 删除培训业务
  const handleDelete = async (business: TrainingBusiness) => {
    try {
      await deleteTrainingBusiness(business.id);
      message.success('删除培训业务成功');
      loadBusinesses();
      loadStats();
    } catch (err) {
      console.error('删除失败:', err);
      message.error(err instanceof Error ? err.message : '删除失败');
    }
  };

  // 删除全部培训业务
  const handleDeleteAll = async () => {
    try {
      const confirmed = await showConfirmDialog(
        '确定要删除所有培训业务数据吗？\n此操作不可恢复，且如果有培训标准依赖这些业务，将无法删除。'
      );
      
      if (!confirmed) {
        return;
      }

      const result = await deleteAllTrainingBusinesses();
      message.success(`已删除 ${result.deleted} 条培训业务记录`);
      loadBusinesses();
      loadStats();
    } catch (err) {
      console.error('删除全部培训业务失败:', err);
      message.error(err instanceof Error ? err.message : '删除全部培训业务失败');
    }
  };

  // 导出培训业务数据
  const handleExport = async () => {
    try {
      const allBusinesses = await exportTrainingBusinesses();
      exportTrainingBusinessesToExcel(allBusinesses, `培训业务数据_${new Date().toLocaleDateString()}.xlsx`);
      message.success('培训业务数据导出成功');
    } catch (err) {
      console.error('导出培训业务数据失败:', err);
      message.error(err instanceof Error ? err.message : '导出培训业务数据失败');
    }
  };

  // 表格列定义
  const columns: ColumnsType<TrainingBusiness> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '业务名称',
      dataIndex: 'name',
      key: 'name',
      width: 400,
      render: (text: string) => <strong>{text}</strong>,
    },
    // {
    //   title: '描述',
    //   dataIndex: 'description',
    //   key: 'description',
    //   ellipsis: true,
    //   render: (text: string) => text || '-',
    // },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => formatDateTime(date),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record: TrainingBusiness) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个培训业务吗？"
            description="删除后相关的培训标准也将失效"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="培训业务总数"
              value={totalCount}
              loading={statsLoading}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              新增培训业务
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadBusinesses();
                loadStats();
              }}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              导出数据
            </Button>
            <Popconfirm
              title="删除确认"
              description="确定要删除所有培训业务数据吗？此操作不可恢复！"
              onConfirm={handleDeleteAll}
              okText="确定"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<ClearOutlined />}
              >
                删除全部
              </Button>
            </Popconfirm>
            <Input.Search
              placeholder="搜索业务名称..."
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
          </Space>
        </div>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={businesses}
          rowKey="id"
          loading={loading}
          scroll={{ x: 800 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
              }
            },
          }}
        />
      </Card>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingBusiness ? '编辑培训业务' : '新增培训业务'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          {editingBusiness ? (
            // 编辑模式 - 单个业务
            <Form.Item
              name="name"
              label="业务名称"
              rules={[
                { required: true, message: '请输入业务名称' },
                { max: 100, message: '业务名称不能超过100个字符' },
              ]}
            >
              <Input placeholder="请输入培训业务名称" />
            </Form.Item>
          ) : (
            // 新增模式 - 批量输入
            <Form.Item
              name="name"
              label="批量添加培训业务"
              rules={[
                { required: true, message: '请输入业务名称' },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="请输入培训业务名称，多个业务使用 、 分隔
例如：GYB培训、SYB培训、创业模拟实训、企业管理培训、电商培训"
                maxLength={1000}
                showCount
              />
            </Form.Item>
          )}

          <Form.Item
            name="description"
            label="业务描述"
            rules={[
              { max: 500, message: '描述不能超过500个字符' },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="请输入培训业务的详细描述（可选）"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>

        <Alert
          message={editingBusiness ? "编辑说明" : "批量添加说明"}
          description={
            editingBusiness ? (
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>编辑单个培训业务的基本信息</li>
                <li>业务名称用于后续培训标准的关联配置</li>
              </ul>
            ) : (
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li><strong>批量输入：</strong>支持同时添加多个培训业务</li>
                <li><strong>分隔符：</strong>使用中文顿号 "、" 分隔多个业务名称</li>
                <li><strong>业务名称：</strong>输入什么名称就保存什么名称，用于后期数据匹配</li>
                <li><strong>业务描述：</strong>批量添加时，所有业务共享相同的描述</li>
                <li><strong>示例输入：</strong>GYB培训、SYB培训、创业模拟实训、企业管理培训、电商培训</li>
              </ul>
            )
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Modal>
    </div>
  );
};

export default TrainingBusinesses;